import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {MaterialNode} from "#template/nodes/material/material-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BooleanLike, JsonLike, MaterialLikeNode, NumberLike, StringLike} from "#template/nodes/types"
import {booleanLike} from "#template/nodes/boolean/fundamental"
import {jsonLike} from "#template/nodes/json/fundamental"
import {materialLike} from "#template/nodes/material/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import {stringLike} from "#template/nodes/string/fundamental"
import {DataObjectReference} from "#template/nodes/image/data-object-reference"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {MaterialGraph} from "@cm/material-nodes"
import {GetFontMaterial} from "#template/runtime-graph/nodes/get-font-material"
import {LoadImageData} from "#template/runtime-graph/nodes/get-image-data"
import {visitNone} from "#template/template-node/visit-helpers"

export type FontMaterialAlignment = "left" | "center" | "right"

export type FontMaterialParameters = {
    name: string
    baseMaterial?: MaterialLikeNode
    glyphImage: DataObjectReference | null
    glyphsConfiguration: JsonLike | null
    glyphHeightCm: NumberLike | null
    text: StringLike | null
    alignment: FontMaterialAlignment
    spacing?: NumberLike
    stamped: BooleanLike
    lightAngle?: NumberLike
    shadowStrength?: NumberLike
    lightStrength?: NumberLike
    embossingSteepness?: NumberLike
    embossingOffset?: NumberLike
}

export type FontMaterialConstructorParameters = Omit<FontMaterialParameters, "alignment" | "stamped"> & {
    alignment?: FontMaterialAlignment
    stamped?: BooleanLike
}

const fontMaterialDefinition: TemplateNodeDefinition<FontMaterialParameters, FontMaterialConstructorParameters> = {
    nodeClass: "FontMaterial",
    validation: {
        parameters: z.object({
            name: z.string(),
            baseMaterial: z.optional(materialLike),
            glyphImage: z.nullable(graphNode(DataObjectReference)),
            glyphsConfiguration: z.nullable(jsonLike),
            glyphHeightCm: z.nullable(numberLike),
            text: z.nullable(stringLike),
            alignment: z._default(z.enum(["left", "center", "right"]), "center"),
            spacing: z.optional(numberLike),
            stamped: z._default(booleanLike, false),
            lightAngle: z.optional(numberLike),
            shadowStrength: z.optional(numberLike),
            lightStrength: z.optional(numberLike),
            embossingSteepness: z.optional(numberLike),
            embossingOffset: z.optional(numberLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class FontMaterial extends MaterialNode<FontMaterialParameters, FontMaterialConstructorParameters> {
    public override get definition() {
        return fontMaterialDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<FontMaterialParameters> {
        const {glyphImage, glyphsConfiguration, text, glyphHeightCm} = this.parameters
        if (glyphImage === null || glyphsConfiguration === null || text === null || glyphHeightCm === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MaterialGraph | null> {
        const [textValid, textInvalid] = scope.branch(evaluator.evaluate(this.parameters.text))
        const [dataObjectValid, dataObjectInvalid] = scope.branch(this.parameters.glyphImage?.getDataObject(scope, evaluator) ?? null)
        const [glyphsConfigurationValid, glyphsConfigurationInvalid] = scope.branch(evaluator.evaluate(this.parameters.glyphsConfiguration))
        const [glyphHeightCmValid, glyphHeightCmInvalid] = scope.branch(evaluator.evaluate(this.parameters.glyphHeightCm))

        const {templateContext} = evaluator
        const {sceneManager} = templateContext

        const {imageData} = scope.node(LoadImageData, {
            sceneManager,
            dataObject: dataObjectValid,
        })

        const {stamped, lightAngle, shadowStrength, lightStrength, embossingSteepness, embossingOffset, alignment} = this.parameters

        const {outputMaterial} = scope.node(GetFontMaterial, {
            baseMaterial: evaluator.evaluate(this.parameters.baseMaterial ?? null),
            glyphsImageData: imageData,
            glyphsConfiguration: glyphsConfigurationValid,
            text: textValid,
            alignment,
            glyphHeightCm: glyphHeightCmValid,
            spacing: evaluator.evaluate(this.parameters.spacing ?? null),
            stamped: scope.pureLambda(evaluator.evaluate(stamped ?? null), (stamped) => stamped ?? false, "stamped"),
            lightAngle: evaluator.evaluate(lightAngle ?? null),
            shadowStrength: evaluator.evaluate(shadowStrength ?? null),
            lightStrength: evaluator.evaluate(lightStrength ?? null),
            embossingSteepness: evaluator.evaluate(embossingSteepness ?? null),
            embossingOffset: evaluator.evaluate(embossingOffset ?? null),
        })

        return scope.phi(outputMaterial, textInvalid, dataObjectInvalid, glyphsConfigurationInvalid, glyphHeightCmInvalid)
    }
}
